import { formatBitrate, formatBytes, type PlaybackStats, type Session } from '../lib/api'
import { Sparkline } from './Sparkline'

export type Sample = { downKbps: number; speed: number }

type Props = {
  session: Session
  playback: PlaybackStats | null
  history: Sample[]
}

const STATUS_LABEL: Record<string, string> = {
  starting: 'Arrencant',
  ready: 'En emissió',
  error: 'Error',
  stopped: 'Aturada',
}

/** What the engine's terse status codes actually mean. */
const ENGINE_STATUS: Record<string, string> = {
  dl: 'descarregant',
  prebuf: 'preemplenant buffer',
  buf: 'buffering',
  check: 'verificant',
  idle: 'inactiu',
  starting: 'arrencant',
}

type Health = { tone: 'ok' | 'warn' | 'bad'; text: string }

/**
 * One honest sentence about the stream, derived from whichever layer is
 * actually the bottleneck. Ordered worst-first so the real cause wins.
 */
function assessHealth(session: Session, playback: PlaybackStats | null): Health {
  const { stat, progress, media } = session

  if (stat && stat.peers === 0) {
    return { tone: 'bad', text: 'Cap peer: ningú està compartint aquest canal ara mateix' }
  }
  if (progress && progress.speed > 0 && progress.speed < 0.97) {
    return {
      tone: 'bad',
      text: `El remux va a ${progress.speed.toFixed(2)}x, per sota del temps real: el swarm no dona prou cabal i acumularàs retard`,
    }
  }
  if (stat && stat.speedDown === 0 && stat.peers > 0) {
    return { tone: 'warn', text: `Hi ha ${stat.peers} peers però no baixa res: probablement peers morts` }
  }
  if (playback && playback.playing && playback.bufferSeconds < 2) {
    return { tone: 'warn', text: `Buffer baix (${playback.bufferSeconds.toFixed(1)} s): es pot tallar` }
  }
  if (media && !media.chromecastSafe && media.chromecastNote) {
    return { tone: 'warn', text: media.chromecastNote }
  }
  const missing = session.clients.reduce((sum, c) => sum + c.misses, 0)
  if (missing > 0) {
    return {
      tone: 'bad',
      text: `${missing} segments demanats que no existien (404). Si emets a un Chromecast, això el trenca sense recuperació.`,
    }
  }
  if (progress && progress.dropped > 0) {
    return { tone: 'warn', text: `ffmpeg ha descartat ${progress.dropped} frames` }
  }
  if (session.status === 'ready') {
    return { tone: 'ok', text: 'El stream va fi: cabal suficient i remux en temps real' }
  }
  return { tone: 'warn', text: 'Encara sense dades suficients per valorar la qualitat' }
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="metric" title={hint}>
      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
    </div>
  )
}

function Group({ title, children, aside }: { title: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <section className="mgroup">
      <header className="mgroup__head">
        <h3>{title}</h3>
        {aside}
      </header>
      <div className="mgroup__grid">{children}</div>
    </section>
  )
}

export function Metrics({ session, playback, history }: Props) {
  const { stat, progress, output, media } = session
  const health = assessHealth(session, playback)
  const uptime = session.readyAt ? (Date.now() - session.readyAt) / 1000 : 0

  const downSeries = history.map((h) => h.downKbps)
  const speedSeries = history.map((h) => h.speed)

  return (
    <div className="metrics">
      <div className="metrics__top">
        <span className={`stats__badge stats__badge--${session.status}`}>
          {STATUS_LABEL[session.status] ?? session.status}
        </span>
        <span className={`health health--${health.tone}`}>{health.text}</span>
      </div>

      <Group
        title="Swarm P2P"
        aside={
          downSeries.length > 1 ? (
            <Sparkline values={downSeries} title="baixada" />
          ) : undefined
        }
      >
        {stat ? (
          <>
            <Metric label="Peers" value={String(stat.peers)} hint="Fonts que ens estan enviant dades" />
            <Metric label="Baixada" value={formatBitrate(stat.speedDown * 1024)} />
            <Metric label="Pujada" value={formatBitrate(stat.speedUp * 1024)} />
            <Metric label="Descarregat" value={formatBytes(stat.downloaded)} />
            <Metric label="Compartit" value={formatBytes(stat.uploaded)} />
            <Metric
              label="Estat engine"
              value={ENGINE_STATUS[stat.status] ?? stat.status}
              hint="Estat intern de l'AceStream Engine"
            />
          </>
        ) : (
          <p className="mgroup__empty">Esperant estadístiques de l'engine…</p>
        )}
      </Group>

      <Group
        title="Remux ffmpeg"
        aside={
          speedSeries.length > 1 ? (
            <Sparkline
              values={speedSeries}
              ceiling={1.5}
              reference={1}
              color={progress && progress.speed < 0.97 ? 'var(--danger)' : 'var(--ok)'}
              title="velocitat de remux"
            />
          ) : undefined
        }
      >
        {progress ? (
          <>
            <Metric
              label="Velocitat"
              value={`${progress.speed.toFixed(2)}x`}
              hint="1.00x = segueix el temps real. Per sota, s'endarrereix."
            />
            <Metric
              label="Bitrate sortida"
              value={output && output.bitrateKbps > 0 ? `${(output.bitrateKbps / 1000).toFixed(2)} Mbps` : '—'}
              hint="Mesurat sobre els segments HLS escrits"
            />
            <Metric
              label="Descartats"
              value={`${progress.dropped} / ${progress.duplicated} dup`}
              hint="Frames que ffmpeg ha hagut de descartar o duplicar"
            />
            <Metric
              label="Segments"
              value={output ? `${output.segments} × ${output.segmentSeconds.toFixed(1)}s` : '—'}
            />
            <Metric
              label="Emès"
              value={output ? formatBytes(output.bytesEmitted / 1024) : '—'}
              hint="Total de dades HLS servides des que va arrencar"
            />
            <Metric label="Temps" value={`${Math.floor(uptime / 60)} min ${Math.floor(uptime % 60)} s`} />
          </>
        ) : (
          <p className="mgroup__empty">Esperant telemetria de ffmpeg…</p>
        )}
      </Group>

      <Group title="Contingut">
        {media ? (
          <>
            <Metric
              label="Vídeo"
              value={`${media.videoCodec ?? '?'}${media.videoProfile ? ` (${media.videoProfile})` : ''}`}
            />
            <Metric label="Resolució" value={media.width && media.height ? `${media.width}×${media.height}` : '?'} />
            <Metric label="FPS origen" value={media.fps ? String(media.fps) : '?'} />
            <Metric
              label="Àudio origen"
              value={`${media.audioCodec ?? 'cap'}${media.audioChannels ? ` ${media.audioChannels}ch` : ''}`}
              hint="Sempre es recodifica a AAC per compatibilitat amb Chromecast"
            />
            <Metric
              label="Vídeo a la tele"
              value={
                { none: 'còpia directa', checking: 'comprovant…', starting: 'preparant GPU…', ready: 'recodificat (GPU)', failed: 'còpia (GPU fallida)' }[session.tv]
              }
              hint="Si el canal no porta fotogrames IDR, el Chromecast no hi pot entrar i se li prepara una versió recodificada per GPU. El mòbil sempre rep la còpia."
            />
            <Metric
              label="Chromecast"
              value={media.chromecastSafe ? 'compatible' : 'problemàtic'}
              hint={media.chromecastNote ?? 'El vídeo es copia sense transcodificar'}
            />
          </>
        ) : (
          <p className="mgroup__empty">Identificant còdecs…</p>
        )}
      </Group>

      {session.clients.length > 0 && (
        <Group title="Clients que consumeixen l'HLS">
          {session.clients.map((client) => (
            <Metric
              key={client.ip}
              label={client.ip}
              value={`${client.requests} pet.${client.misses > 0 ? ` · ${client.misses} × 404` : ''}`}
              hint={
                client.misses > 0
                  ? 'Els 404 són fatals per a un Chromecast: vol dir que demana segments que no existeixen'
                  : 'Cap error'
              }
            />
          ))}
        </Group>
      )}

      <Group title="Reproductor local">
        {playback ? (
          <>
            <Metric label="Buffer" value={`${playback.bufferSeconds.toFixed(1)} s`} />
            <Metric
              label="Frames perduts"
              value={
                playback.totalFrames > 0
                  ? `${playback.droppedFrames} (${((playback.droppedFrames / playback.totalFrames) * 100).toFixed(2)}%)`
                  : String(playback.droppedFrames)
              }
            />
            <Metric label="Amplada estimada" value={formatBitrate(playback.bandwidthEstimate / 8)} />
            <Metric label="Talls" value={String(playback.stalls)} hint="Vegades que el reproductor s'ha quedat sense dades" />
          </>
        ) : (
          <p className="mgroup__empty">Reprodueix al navegador per veure aquestes mètriques</p>
        )}
      </Group>
    </div>
  )
}
